import { FC } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import NotFound from 'components/NotFound';
import PageWithSidebar from 'components/PageWithSidebar';
import EntitySidebar from 'components/EntitySidebar';
import ServiceOptionEditForm from 'components/ServiceOptionEditForm';

import { withData } from 'lib';
import { SettingsServiceOptionEditPageQuery } from '__generated__/SettingsServiceOptionEditPageQuery.graphql';
import EntityEditPageSkeleton from 'components/EntityEditPageSkeleton';
import FormSkeleton from 'components/FormSkeleton';

const query = graphql`
  query SettingsServiceOptionEditPageQuery($id: ID!) {
    serviceOption(id: $id) {
      name

      service {
        id
        name

        group {
          id
          name
        }
      }

      ...EntitySidebar_entity
      ...ServiceOptionEditForm_serviceOption
    }
  }
`;

type Props = {
  preloadedQuery: PreloadedQuery<SettingsServiceOptionEditPageQuery>;
};

type RouteParams = {
  id: string;
};

const breadcrumbs = [
  { link: '/settings', name: 'Nustatymai' },
  {
    link: '/settings/service-groups',
    name: 'Papildomų paslaugų paketai',
  },
];

const SettingsServiceOptionEditPage: FC<Props> = ({ preloadedQuery }) => {
  const { serviceOption } =
    usePreloadedQuery<SettingsServiceOptionEditPageQuery>(
      query,
      preloadedQuery
    );

  if (!serviceOption) {
    return <NotFound />;
  }

  return (
    <PageWithSidebar
      sidebar={<EntitySidebar entity={serviceOption} />}
      breadcrumbs={[
        ...breadcrumbs,
        {
          link: `/settings/service-groups/${serviceOption.service.group.id}#services`,
          name: serviceOption.service.group.name,
        },
        {
          link: `/settings/services/${serviceOption.service.id}#options`,
          name: serviceOption.service.name,
        },
        { name: serviceOption.name },
      ]}>
      <ServiceOptionEditForm serviceOption={serviceOption} />
    </PageWithSidebar>
  );
};

export default withData<SettingsServiceOptionEditPageQuery, RouteParams>({
  query,

  loading: (
    <EntityEditPageSkeleton breadcrumbs={breadcrumbs}>
      <FormSkeleton sections={1} />
    </EntityEditPageSkeleton>
  ),

  variables: ({ routeParams }) => ({
    id: routeParams.id,
  }),
})(SettingsServiceOptionEditPage);
